import { Download, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import AppButton from "../../components/ui/AppButton";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import DateRangePicker from "../../components/ui/DateRangePicker";
import { formatDateDDMMYYYY } from "../../lib/utils";
import { Audit, AuditSearchRequest, AuditSearchResponse } from "./@types";
import { useGetCompanyAuditMutation } from "./transactionApiSlice";

const AuditDashboard: React.FC = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [dateAddedFilter, setDateAddedFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dates, setDates] = useState({
    startDate: "",
    endDate: "",
  });

  // RTK Query mutation
  const [getTransaction, { data: auditData, isLoading }] =
    useGetCompanyAuditMutation();

  const fetchTransaction = useCallback(async () => {
    const requestBody: AuditSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      startDate: dates.startDate || undefined,
      endDate: dates.endDate || undefined,
      searchTerm: searchTerm || undefined,
    };

    try {
      await getTransaction(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch audit:", err);
    }
  }, [currentPage, pageSize, searchTerm, dates, getTransaction]);

  // Fetch audit on component mount and when filters change
  useEffect(() => {
    fetchTransaction();
  }, [currentPage, pageSize, searchTerm, dateAddedFilter, fetchTransaction]);

  // Table columns configuration
  const columns: Column<Audit>[] = [
    {
      key: "userName",
      header: "User",
      width: "",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "tableName",
      header: "Model",
      width: "24",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "action",
      header: "action",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "dateTime",
      header: "Timestamp",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{formatDateDDMMYYYY(value)}</span>
      ),
    },
    {
      key: "ipAddress",
      header: "IP Address",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleExportCSV = () => {
    console.log("Export CSV functionality");
  };

  const response = auditData as AuditSearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">Audit log</h2>
          </div>
        </div>

        {/* All audit section */}
        <div className="space-y-6 bg-white rounded-[10px] p-4">
          {/* Filters and Actions */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-[16px] pl-9 pr-4 py-2 border border-[#F6F6F6] bg-[#F6F6F6] rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-full sm:w-[367px] h-[42px]"
                />
              </div>

              <DateRangePicker value={dates} onChange={setDates} />
            </div>

            <div className="flex">
              <AppButton
                appearance="soft"
                onClick={handleExportCSV}
                className="flex items-center px-4 py-2 border border-primary bg-white rounded-lg text-primary hover:bg-gray-50 transition-colors min-w-[146px] h-[38px]"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </AppButton>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              emptyMessage="No audit found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditDashboard;
